/**
 * CandidateAvatar (v2) — the preserved CertAIn candidate-face signature.
 *
 * v2 does NOT flatten candidates into a faceless enterprise table
 * (design/v2-direction/README.md §3). This wraps the vendored shadcn
 * `<Avatar>` and reuses the app's real avatar data path:
 *   - `src` → photo (Radix `<AvatarImage>`, falls back on load error)
 *   - fallback → deterministic initials on the hash-derived brand gradient,
 *     the SAME `avatarColor()` / `initials()` / `shade()` from lib/initials.ts
 *     that the default `<Avatar>` uses (AVATAR_COLORS palette, blue/navy-forward
 *     entries lead the hash so a grid of faces reads CertAIn blue).
 *
 * Optional `ring` renders the ONE sparing teal signature moment — the
 * candidate-hero avatar ring (`--accent-secondary` = teal). Use it ONLY on the
 * candidate-detail hero, never on list rows (teal stays sparing per README §2).
 *
 * Token discipline: the gradient is composed from lib/initials.ts (the
 * sanctioned JS color source); no hex literals in this file. Guard-clean.
 */
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { avatarColor, initials, shade } from "@/lib/initials";
import { cn } from "@/lib/utils";

type CandidateAvatarSize = "xs" | "sm" | "md" | "lg" | "xl";

const SIZE_CLASS: Record<CandidateAvatarSize, string> = {
  xs: "h-7 w-7 text-[10px]", // 28px — canonical dashboard top-candidate face
  sm: "h-8 w-8 text-xs",
  md: "h-10 w-10 text-sm",
  lg: "h-12 w-12 text-base",
  xl: "h-[72px] w-[72px] text-xl", // the README §3 72px card/hero face
};

interface CandidateAvatarProps {
  /** Candidate name — drives initials text + deterministic gradient color. */
  name: string;
  /** Photo URL. Null/undefined or load failure → gradient-initials fallback. */
  src?: string | null;
  size?: CandidateAvatarSize;
  /** Render the sparing teal hero ring (candidate-detail hero ONLY). */
  ring?: boolean;
  className?: string;
}

export function CandidateAvatar({
  name,
  src,
  size = "md",
  ring = false,
  className,
}: CandidateAvatarProps): JSX.Element {
  const base = avatarColor(name);
  const stop = shade(base, -15);
  const gradient = `linear-gradient(135deg, ${base} 0%, ${stop} 100%)`;

  return (
    <Avatar
      className={cn(
        SIZE_CLASS[size],
        // The sparing teal signature ring (--accent-secondary). CSS var, not hex.
        ring && "ring-2 ring-accent-secondary ring-offset-2 ring-offset-surface-card",
        className,
      )}
    >
      {src ? <AvatarImage src={src} alt={name} /> : null}
      <AvatarFallback style={{ backgroundImage: gradient }}>
        {initials(name)}
      </AvatarFallback>
    </Avatar>
  );
}
